<template>
  <div class="purchase-form">
    <el-form ref="form" :model="form" label-position="top" :rules="rules">
      <the-heading>
        {{ isPeriodic ? $t('ec-line003.tab.subscription') : $t('ec-line003.tab.normal') }}
        <br />
        {{ !!user ? $t('ec-line029.headingOrder') : $t('ec-line029.heading') }}
      </the-heading>

      <div v-safe-html="purchaseContent1"></div>

      <the-section v-if="!user">
        <p class="text-lg leading-5 font-black text-center mb-8">
          {{ $t('ec-line029.loginForm.note') }}
        </p>
        <div v-if="lineChannelRequire">
          <div class="flex justify-center">
            <line-button class="rounded-full font-black w-60 mb-8" @click="loginLine">{{
              $t('ec-line031.content.form.lineLogin')
            }}</line-button>
          </div>

          <div class="purchase-form__line-label mb-8">
            <span class="content">{{ $t('ec-line029.loginForm.or') }}</span>
          </div>
        </div>

        <login-form @on-login="loginByMail"></login-form>
      </the-section>

      <div v-safe-html="purchaseContent2"></div>

      <the-section
        class="section-amazon"
        :note-title="!user ? $t('ec-line029.noteFirstPurchase') : null"
        :title="$t('ec-line029.content.paymentWithAmazonAccountOptional')"
        v-show="hasAmazonPayment"
      >
        <div class="flex flex-row justify-between items-center flex-wrap lg:flex-nowrap">
          <p class="flex-grow lg:flex-initial text-base">{{ $t('ec-line029.content.paymentWithAmazonAccountTip') }}</p>
          <div class="section-amazon__button">
            <div :class="['purchase-form__payment-btn', { 'cursor-not-allowed opacity-60': disableAmazon }]">
              <div
                v-show="!isLoginAmazon"
                id="AmazonPayButton"
                class="section-amazon__button-login"
                @click="(event) => handleAmazonButton(event, Constants.AMAZON.BACKUP_AMAPAY_029)"
                :class="{ 'pointer-events-none': disableAmazon }"
              ></div>
              <amazon-logout-button
                v-show="isLoginAmazon"
                @click="
                  onClickAmazonLogout(
                    amazonProductType,
                    ['#AmazonPayButton', '#AmazonPayButton2'],
                    Constants.AMAZON.ACCESS_TOKEN_029
                  )
                "
              />
            </div>
            <p v-show="!isLoginAmazon && disableAmazon" class="text-red-150 text-xs mt-1">
              {{ hintAmazon }}
            </p>
          </div>
        </div>
      </the-section>

      <!-- Member Information -->
      <the-section
        ref="memberInformation"
        :class="{ 'mt-0': !user }"
        :title="!user ? $t('ec-line029.content.registrationMember') : $t('ec-line029.content.memberInformation')"
        :sub-title="$t('common.requiredItem')"
      >
        <template #subTitle>
          <span v-if="!isViewMode || isEdit(section.memberInfo)">{{ $t('common.requiredItem') }}</span>
          <el-button class="section__button" v-else @click="onEdit(section.memberInfo)">{{
            $t('common.action.edit')
          }}</el-button>
        </template>

        <el-form-item
          v-if="isViewMode && !isEdit(section.memberInfo) && businessAccountUseFlag"
          :label="$t('common.label.accountType')"
          required
        >
          <span>
            {{ form.memberInformation.businessAccountFlag ? $t('common.label.business') : $t('common.label.personal') }}
          </span>
        </el-form-item>
        <el-form-item v-else-if="businessAccountUseFlag" :label="$t('common.label.accountType')" required>
          <div class="flex flex-col">
            <el-radio-group v-model="form.memberInformation.businessAccountFlag" :disabled="user" class="mb-1">
              <el-radio class="radio-dot" :label="Constants.BUSINESS_ACCOUNT_FLAG.PERSONAL">
                {{ $t('common.label.personal') }}
              </el-radio>
              <el-radio class="radio-dot" :label="Constants.BUSINESS_ACCOUNT_FLAG.BUSINESS">
                {{ $t('common.label.business') }}
              </el-radio>
            </el-radio-group>
          </div>
        </el-form-item>

        <el-form-item
          v-show="corporateFlag"
          :label="$t('common.memberInformation.companyName')"
          prop="memberInformation.companyName"
          class="is-required"
          :rules="companyNameRules"
          :required="form.memberInformation.businessAccountFlag"
        >
          <el-input
            v-if="!isViewMode || isEdit(section.memberInfo)"
            type="text"
            v-model="form.memberInformation.companyName"
            :placeholder="$t('common.informationPlaceholder.companyName')"
            :maxlength="16"
          ></el-input>
          <span v-else>
            <div>{{ form.memberInformation.companyName }}</div>
          </span>
        </el-form-item>

        <span v-if="corporateFlag || !form.memberInformation.businessAccountFlag">
          <el-form-item :label="$t('ec-line029.content.fullName')" required>
            <el-row v-if="!isViewMode || isEdit(section.memberInfo)" :gutter="16">
              <el-col :span="12">
                <el-form-item prop="memberInformation.lastName">
                  <el-input
                    type="text"
                    v-model="form.memberInformation.lastName"
                    :placeholder="$t('common.informationPlaceholder.lastNamePlaceholder')"
                    :maxlength="[form.memberInformation.businessAccountFlag ? 7 : 16]"
                  ></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item prop="memberInformation.firstName">
                  <el-input
                    type="text"
                    v-model="form.memberInformation.firstName"
                    :placeholder="$t('common.informationPlaceholder.firstNamePlaceholder')"
                    :maxlength="[form.memberInformation.businessAccountFlag ? 7 : 20]"
                  ></el-input>
                </el-form-item>
              </el-col>
            </el-row>
            <span v-else>{{
              Utils.helper.fullName(form.memberInformation.lastName, form.memberInformation.firstName)
            }}</span>
          </el-form-item>

          <el-form-item :label="$t('ec-line029.content.nameKana')" required>
            <el-row v-if="!isViewMode || isEdit(section.memberInfo)" :gutter="16">
              <el-col :span="12">
                <el-form-item prop="memberInformation.lastNameKana">
                  <el-input
                    type="text"
                    v-model="form.memberInformation.lastNameKana"
                    :placeholder="$t('common.informationPlaceholder.lastNameKanaPlaceholder')"
                    :maxlength="40"
                  ></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item prop="memberInformation.firstNameKana">
                  <el-input
                    type="text"
                    v-model="form.memberInformation.firstNameKana"
                    :placeholder="$t('common.informationPlaceholder.firstNameKanaPlaceholder')"
                    :maxlength="40"
                  ></el-input>
                </el-form-item>
              </el-col>
            </el-row>
            <span v-else>{{
              Utils.helper.fullName(form.memberInformation.lastNameKana, form.memberInformation.firstNameKana)
            }}</span>
          </el-form-item>
          <div></div>
        </span>
        <span v-else>
          <el-form-item
            :label="$t('common.memberInformation.companyName')"
            prop="memberInformation.companyName"
            class="is-required"
            :rules="rules"
            required
          >
            <span v-if="!isViewMode || isEdit(section.memberInfo)" class="add-msg-area">
              <el-input
                v-model="form.memberInformation.companyName"
                :placeholder="$t('common.informationPlaceholder.exampleCompanyName')"
                :maxlength="32"
              ></el-input>
              <div class="form-item-explanation">
                {{ $t('common.messages.CompanyNameMessage') }}
              </div>
            </span>
            <span v-else>{{
              Utils.helper.fullName(form.memberInformation.lastName, form.memberInformation.firstName)
            }}</span>
          </el-form-item>
          <el-form-item
            :label="$t('common.memberInformation.companyNameKana')"
            prop="memberInformation.companyNameKana"
            class="is-required"
            :rules="rules"
            required
          >
            <span v-if="!isViewMode || isEdit(section.memberInfo)" class="add-msg-area">
              <el-input
                v-model="form.memberInformation.companyNameKana"
                :placeholder="$t('common.informationPlaceholder.exampleCompanyNameKana')"
                :maxlength="32"
              ></el-input>
              <div class="form-item-explanation">
                {{ $t('common.messages.CompanyNameKanaMessage') }}
              </div>
            </span>
            <span v-else>{{
              Utils.helper.fullName(form.memberInformation.lastName, form.memberInformation.firstName)
            }}</span>
          </el-form-item>
          <div></div>
        </span>
        <el-form-item
          v-if="
            isViewMode &&
            !isEdit(section.memberInfo) &&
            !form.memberInformation.businessAccountFlag &&
            form.memberInformation.birthdayYear
          "
          :label="$t('ec-line029.content.birthday')"
          required
        >
          <span>{{
            form.memberInformation.birthdayYear +
            '/' +
            form.memberInformation.birthdayMonth +
            '/' +
            form.memberInformation.birthdayDay
          }}</span>
        </el-form-item>
        <el-form-item
          v-else-if="(!isViewMode || isEdit(section.memberInfo)) && !form.memberInformation.businessAccountFlag"
          :label="$t('ec-line029.content.birthday')"
          class="birthday-form"
        >
          <el-row :gutter="6" type="flex">
            <el-col :span="8">
              <el-form-item prop="memberInformation.birthdayYear">
                <el-select
                  v-model="form.memberInformation.birthdayYear"
                  :placeholder="$t('common.informationPlaceholder.yearPlaceholder')"
                >
                  <el-option
                    v-for="item in Constants.DATE_OPTIONS.YEAR"
                    :key="item"
                    :value="item"
                    :label="item"
                  ></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="1">
              <div class="text-center">{{ $t('common.dateSeparator') }}</div>
            </el-col>
            <el-col :span="7">
              <el-form-item prop="memberInformation.birthdayMonth">
                <el-select
                  v-model="form.memberInformation.birthdayMonth"
                  :placeholder="$t('common.informationPlaceholder.monthPlaceholder')"
                >
                  <el-option
                    v-for="item in Constants.DATE_OPTIONS.MONTH"
                    :key="item"
                    :value="item"
                    :label="item"
                  ></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="1">
              <div class="text-center">{{ $t('common.dateSeparator') }}</div>
            </el-col>
            <el-col :span="7">
              <el-form-item prop="memberInformation.birthdayDay">
                <el-select
                  v-model="form.memberInformation.birthdayDay"
                  :placeholder="$t('common.informationPlaceholder.dayPlaceholder')"
                >
                  <el-option v-for="item in days" :key="item" :value="item" :label="item"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
        </el-form-item>

        <el-form-item
          v-if="
            isViewMode &&
            !isEdit(section.memberInfo) &&
            !form.memberInformation.businessAccountFlag &&
            form.memberInformation.sex
          "
          :label="$t('ec-line029.content.sex')"
          prop="memberInformation.sex"
          required
        >
          <span>{{
            form.memberInformation.sex
              ? $t(`common.sex.${$_.camelCase(Utils.helper.valueTokey(Constants.SEX, form.memberInformation.sex))}`)
              : ''
          }}</span>
        </el-form-item>
        <el-form-item
          v-else-if="(!isViewMode || isEdit(section.memberInfo)) && !form.memberInformation.businessAccountFlag"
          :label="$t('ec-line029.content.sex')"
          prop="memberInformation.sex"
          required
        >
          <el-radio-group v-model="form.memberInformation.sex">
            <el-radio v-for="item in sex" :key="item.value" class="radio-dot" :label="item.value">{{
              item.label
            }}</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item :label="$t('ec-line029.content.email')" prop="memberInformation.email">
          <el-input
            v-if="!isViewMode || isEdit(section.memberInfo)"
            type="text"
            v-model="form.memberInformation.email"
            :placeholder="$t('common.informationPlaceholder.emailPlaceholder')"
            :maxlength="256"
            autocomplete="new-password"
            @blur="detectGuestCartAbandonmentOnBlur('memberInformation.email')"
          ></el-input>
          <span class="break-all" v-else>{{ form.memberInformation.email }}</span>
        </el-form-item>

        <el-form-item
          v-show="!isViewMode || isEdit(section.memberInfo)"
          :label="$t('ec-line029.content.emailConfirm')"
          prop="memberInformation.emailConfirm"
        >
          <el-input
            type="text"
            v-model="form.memberInformation.emailConfirm"
            :placeholder="$t('common.informationPlaceholder.emailPlaceholder')"
            :maxlength="256"
            autocomplete="new-password"
            @blur="detectGuestCartAbandonmentOnBlur('memberInformation.emailConfirm')"
          ></el-input>
        </el-form-item>

        <template v-show="!isRequiredAdd">
          <el-form-item v-if="!isViewMode" :label="$t('ec-line029.content.password')" prop="memberInformation.password">
            <el-input
              v-if="!isViewMode"
              type="password"
              v-model="form.memberInformation.password"
              :placeholder="$t('common.informationPlaceholder.passPlaceholder')"
              :maxlength="256"
              autocomplete="new-password"
            ></el-input>
          </el-form-item>

          <el-form-item
            v-if="!isViewMode"
            :label="$t('ec-line029.content.passwordConfirm')"
            prop="memberInformation.passwordConfirm"
          >
            <el-input
              type="password"
              v-model="form.memberInformation.passwordConfirm"
              :placeholder="$t('common.informationPlaceholder.passPlaceholder')"
              :maxlength="256"
              autocomplete="new-password"
            ></el-input>
          </el-form-item>
        </template>

        <el-form-item
          v-show="!isViewMode || isEdit(section.memberInfo)"
          :label="$t('ec-line029.content.postalCode')"
          class="mb-0 lg:mb-6"
          required
        >
          <div class="flex items-center">
            <el-form-item
              prop="memberPostalCodeRule"
              class="flex items-center postal-code-form member-information-postal-code"
            >
              <div class="flex">
                <el-input
                  inputmode="numeric"
                  v-model="form.memberInformation.postalCode1"
                  :placeholder="$t('common.informationPlaceholder.postalCodeFirstPlaceholder')"
                  :maxlength="3"
                  v-replace-half-width
                  class="postal-code01"
                  @blur="getAddress('memberInformation')"
                  @change="onChangePostalCodeForm('memberInformation')"
                ></el-input>
                <div class="mx-1.5 mt-2 text-center">{{ $t('common.hyphenSeparator') }}</div>
                <el-input
                  inputmode="numeric"
                  v-model="form.memberInformation.postalCode2"
                  :placeholder="$t('common.informationPlaceholder.postalCodeLastPlaceholder')"
                  :maxlength="4"
                  v-replace-half-width
                  class="postal-code02"
                  @blur="getAddress('memberInformation')"
                  @change="onChangePostalCodeForm('memberInformation')"
                ></el-input>
              </div>
            </el-form-item>
            <div class="hidden lg:block leading-none items-center ml-3">
              <span class="text-xs">{{ $t('ec-line029.content.postalCodeTip') }}</span>
            </div>
          </div>
          <div class="lg:hidden mt-5">
            <span class="text-xs">{{ $t('ec-line029.content.postalCodeTip') }}</span>
          </div>
        </el-form-item>

        <el-form-item
          v-show="!isViewMode || isEdit(section.memberInfo)"
          :label="$t('ec-line029.content.prefectures')"
          prop="memberInformation.prefecture"
        >
          <el-select v-model="form.memberInformation.prefecture" :placeholder="$t('common.pleaseSelect')">
            <el-option v-for="item in prefectures" :key="item.id" :value="item.id" :label="item.name"></el-option>
          </el-select>
        </el-form-item>

        <el-form-item
          v-show="!isViewMode || isEdit(section.memberInfo)"
          :label="$t('ec-line029.content.municipalities')"
          prop="memberInformation.municipality"
        >
          <el-input
            type="text"
            v-model="form.memberInformation.municipality"
            :placeholder="$t('common.informationPlaceholder.municipalitiesPlaceholder')"
            :maxlength="40"
            v-replace-half-width
          ></el-input>
        </el-form-item>

        <el-form-item
          v-if="!isViewMode || isEdit(section.memberInfo)"
          :label="!user ? $t('ec-line029.content.address') : $t('ec-line029.content.addressSecond')"
          prop="memberInformation.address"
        >
          <el-input
            v-if="!isViewMode || isEdit(section.memberInfo)"
            type="text"
            v-model="form.memberInformation.address"
            :placeholder="$t('common.informationPlaceholder.addressPlaceholder')"
            :maxlength="40"
            v-replace-half-width
          ></el-input>
        </el-form-item>
        <el-form-item
          v-else
          :label="!user ? $t('ec-line029.content.address') : $t('ec-line029.content.addressSecond')"
          prop="memberInformation.address"
        >
          <div class="text-address">
            <p>
              {{ Utils.helper.postalCode(form.memberInformation.postalCode1, form.memberInformation.postalCode2) }}
            </p>
            <p>
              {{
                Utils.helper.address(
                  getPrefectureFromId(form.memberInformation.prefecture),
                  form.memberInformation.municipality,
                  form.memberInformation.address,
                  ''
                )
              }}
            </p>
            <p>{{ form.memberInformation.buildingApartmentName }}</p>
          </div>
        </el-form-item>

        <el-form-item
          v-show="!isViewMode || isEdit(section.memberInfo)"
          :label="$t('ec-line029.content.buildingApartmentName')"
          prop="memberInformation.buildingApartmentName"
        >
          <el-input
            type="text"
            v-model="form.memberInformation.buildingApartmentName"
            :placeholder="$t('common.informationPlaceholder.buildingApartmentNamePlaceholder')"
            :maxlength="40"
            v-replace-half-width
          ></el-input>
        </el-form-item>

        <el-form-item
          :label="$t('ec-line029.content.phoneNumber')"
          prop="memberInformation.phoneNumber"
          :class="{ 'mb-3': isValidatePhoneNumberFlag }"
          class="input-phone-number"
        >
          <div v-if="!isViewMode || isEdit(section.memberInfo)" class="lg:flex items-center">
            <el-input
              type="text"
              inputmode="numeric"
              v-model="form.memberInformation.phoneNumber"
              :placeholder="$t('common.informationPlaceholder.phoneNumberPlaceholder')"
              :maxlength="11"
              @blur="detectGuestCartAbandonmentOnBlur('memberInformation.phoneNumber')"
            ></el-input>
            <div class="hidden lg:block leading-none ml-3">
              <span class="text-xs">{{ $t('common.messages.phoneNumberInputRules') }}</span>
            </div>
          </div>
          <span v-else>
            <div>{{ form.memberInformation.phoneNumber }}</div>
          </span>
        </el-form-item>
        <div v-if="isValidatePhoneNumberFlag && isViewMode && !isEdit(section.memberInfo)">
          <span class="text-red-250 text-xs">
            {{ $t('ec-line029.content.phoneNumberValidNotice') }}
          </span>
        </div>
        <div class="lg:hidden leading-none items-center">
          <span class="text-xs">{{ $t('common.messages.phoneNumberInputRules') }}</span>
        </div>
        <!-- </fragment> -->
      </the-section>

      <!-- Shipping Information -->
      <the-section
        ref="shippingInformation"
        :title="$t('ec-line029.content.shippingInformation')"
        class="shipping-section"
      >
        <template #subTitle>
          <template v-if="isLoginAmazon">
            <div class="flex flex-col">
              <el-button v-show="!isViewMode" id="changeCustomerAddress" class="section__button">{{
                $t('common.action.editAmazon')
              }}</el-button>
            </div>
          </template>
        </template>

        <fragment v-if="!isViewMode || isEdit(section.shippingInfo) || !addresses.length">
          <el-form-item
            :label="$t('ec-line029.content.selectDeliveryDestination')"
            prop="shippingInformation.deliveryDestination"
          >
            <el-select v-model="form.shippingInformation.deliveryDestination" :placeholder="$t('common.pleaseSelect')">
              <el-option
                v-for="item in deliveryDestinations"
                :key="item.value"
                :value="item.value"
                :label="item.label"
              ></el-option>
            </el-select>
          </el-form-item>

          <fragment v-if="isAddressShippingDiffInfo">
            <div class="form-item-card">
              <span v-if="corporateFlag || !form.memberInformation.businessAccountFlag">
                <el-form-item :label="$t('ec-line029.content.fullName')" required>
                  <el-row :gutter="16">
                    <el-col :span="12">
                      <el-form-item prop="shippingInformation.lastName">
                        <el-input
                          type="text"
                          v-model="form.shippingInformation.lastName"
                          :placeholder="$t('common.informationPlaceholder.lastNamePlaceholder')"
                          :maxlength="16"
                        ></el-input>
                      </el-form-item>
                    </el-col>
                    <el-col :span="12">
                      <el-form-item prop="shippingInformation.firstName">
                        <el-input
                          type="text"
                          v-model="form.shippingInformation.firstName"
                          :placeholder="$t('common.informationPlaceholder.firstNamePlaceholder')"
                          :maxlength="20"
                        ></el-input>
                      </el-form-item>
                    </el-col>
                  </el-row>
                </el-form-item>
                <el-form-item :label="$t('ec-line029.content.nameKana')" required>
                  <el-row :gutter="16">
                    <el-col :span="12">
                      <el-form-item prop="shippingInformation.lastNameKana">
                        <el-input
                          type="text"
                          v-model="form.shippingInformation.lastNameKana"
                          :placeholder="$t('common.informationPlaceholder.lastNameKanaPlaceholder')"
                          :maxlength="40"
                        ></el-input>
                      </el-form-item>
                    </el-col>
                    <el-col :span="12">
                      <el-form-item prop="shippingInformation.firstNameKana">
                        <el-input
                          type="text"
                          v-model="form.shippingInformation.firstNameKana"
                          :placeholder="$t('common.informationPlaceholder.firstNameKanaPlaceholder')"
                          :maxlength="40"
                        ></el-input>
                      </el-form-item>
                    </el-col>
                  </el-row>
                </el-form-item>
              </span>
              <span v-else>
                <el-form-item
                  :label="$t('common.memberInformation.companyName')"
                  prop="shippingInformation.companyName"
                  class="is-required"
                  :rules="companyNameRules"
                  required
                >
                  <el-input
                    v-model="form.shippingInformation.companyName"
                    :placeholder="$t('common.informationPlaceholder.exampleCompanyName')"
                    :maxlength="32"
                  ></el-input>
                </el-form-item>
                <el-form-item
                  :label="$t('common.memberInformation.companyNameKana')"
                  prop="shippingInformation.companyNameKana"
                  class="is-required"
                  required
                >
                  <el-input
                    v-model="form.shippingInformation.companyNameKana"
                    :placeholder="$t('common.informationPlaceholder.exampleCompanyNameKana')"
                    :maxlength="32"
                  ></el-input>
                </el-form-item>
                <div></div>
              </span>
              <div class="flex items-center">
                <el-form-item
                  :label="$t('ec-line029.content.postalCode')"
                  prop="shippingPostalCodeRule"
                  class="flex items-center postal-code-form"
                >
                  <div class="flex shipping-postal-code-form">
                    <el-input
                      inputmode="numeric"
                      v-model="form.shippingInformation.postalCode1"
                      :placeholder="$t('common.informationPlaceholder.postalCodeFirstPlaceholder')"
                      :maxlength="3"
                      v-replace-half-width
                      class="postal-code01"
                      @blur="getAddress('shippingInformation')"
                      @change="onChangePostalCodeForm('shippingInformation')"
                    ></el-input>
                    <div class="mx-1.5 mt-2 text-center">{{ $t('common.hyphenSeparator') }}</div>
                    <el-input
                      inputmode="numeric"
                      v-model="form.shippingInformation.postalCode2"
                      :placeholder="$t('common.informationPlaceholder.postalCodeLastPlaceholder')"
                      :maxlength="4"
                      v-replace-half-width
                      class="postal-code02"
                      @blur="getAddress('shippingInformation')"
                      @change="onChangePostalCodeForm('shippingInformation')"
                    ></el-input>
                  </div>
                </el-form-item>
                <div class="hidden lg:block leading-none items-center mb-6 ml-3">
                  <span class="text-xs">{{ $t('ec-line029.content.postalCodeTip') }}</span>
                </div>
              </div>
              <div class="lg:hidden">
                <span class="text-xs">{{ $t('ec-line029.content.postalCodeTip') }}</span>
              </div>

              <el-form-item :label="$t('ec-line029.content.prefectures')" prop="shippingInformation.prefecture">
                <el-select v-model="form.shippingInformation.prefecture" :placeholder="$t('common.pleaseSelect')">
                  <el-option v-for="item in prefectures" :key="item.id" :value="item.id" :label="item.name"></el-option>
                </el-select>
              </el-form-item>

              <el-form-item :label="$t('ec-line029.content.municipalities')" prop="shippingInformation.municipality">
                <el-input
                  type="text"
                  v-model="form.shippingInformation.municipality"
                  :placeholder="$t('common.informationPlaceholder.municipalitiesPlaceholder')"
                  :maxlength="40"
                  v-replace-half-width
                ></el-input>
              </el-form-item>

              <el-form-item :label="$t('ec-line029.content.address')" prop="shippingInformation.address">
                <el-input
                  type="text"
                  v-model="form.shippingInformation.address"
                  :placeholder="$t('common.informationPlaceholder.addressPlaceholder')"
                  :maxlength="40"
                  v-replace-half-width
                ></el-input>
              </el-form-item>

              <el-form-item
                :label="$t('ec-line029.content.buildingApartmentName')"
                prop="shippingInformation.buildingApartmentName"
              >
                <el-input
                  type="text"
                  v-model="form.shippingInformation.buildingApartmentName"
                  :placeholder="$t('common.informationPlaceholder.buildingApartmentNamePlaceholder')"
                  :maxlength="40"
                  v-replace-half-width
                ></el-input>
              </el-form-item>

              <el-form-item
                :label="$t('ec-line029.content.phoneNumber')"
                prop="shippingInformation.phoneNumber"
                class="input-phone-number"
              >
                <div class="flex lg:items-center w-full">
                  <el-input
                    type="text"
                    inputmode="numeric"
                    v-model="form.shippingInformation.phoneNumber"
                    :placeholder="$t('common.informationPlaceholder.phoneNumberPlaceholder')"
                    :maxlength="11"
                  ></el-input>
                  <div class="hidden lg:flex items-center ml-3">
                    <span class="text-xs">{{ $t('common.messages.phoneNumberInputRules') }}</span>
                  </div>
                </div>
              </el-form-item>
              <div class="lg:hidden leading-none items-center">
                <span class="text-xs">{{ $t('common.messages.phoneNumberInputRules') }}</span>
              </div>
            </div>
          </fragment>
        </fragment>

        <el-form-item v-else :label="$t('common.label.shippingAddressCard')">
          <address-info-item
            :full-name="fullNameAddress"
            :postal-code="
              Utils.helper.postalCode(form.shippingInformation.postalCode1, form.shippingInformation.postalCode2)
            "
            :address="
              Utils.helper.address(
                getPrefectureFromId(form.shippingInformation.prefecture),
                form.shippingInformation.municipality,
                form.shippingInformation.address
              )
            "
            :building-name="form.shippingInformation.buildingApartmentName"
          >
            <template #action>
              <el-button
                v-if="!corporateFlag || (corporateFlag && addresses.length > 1)"
                type="text"
                class="address-info-item__action__button-edit"
                @click="addressDialog = true"
              >
                <span>{{ $t('common.action.changeShippingAddress') }}</span>
              </el-button>
            </template>
          </address-info-item>
        </el-form-item>

        <el-form-item :label="$t('ec-line029.content.shippingMethod')" prop="shippingInformation.shippingMethod">
          <el-select
            v-model="form.shippingInformation.shippingMethod"
            :placeholder="$t('common.pleaseSelect')"
            @change="onUpdateShippingMethod"
          >
            <el-option
              v-for="item in shippingMethods"
              :key="item.value"
              :value="item.value"
              :label="item.label"
            ></el-option>
          </el-select>
        </el-form-item>

        <el-form-item
          :label="$t('ec-line029.content.desiredDeliveryDay')"
          prop="shippingInformation.desiredDeliveryDay"
        >
          <el-select v-model="form.shippingInformation.desiredDeliveryDay" :placeholder="$t('common.pleaseSelect')">
            <el-option
              v-for="(item, index) in desiredDeliveryDays"
              :key="index"
              :value="item.value"
              :label="item.label"
            ></el-option>
          </el-select>
        </el-form-item>

        <template>
          <el-form-item
            v-if="!isMailDeliveryService"
            :label="$t('ec-line029.content.desiredDeliveryTimeZone')"
            prop="shippingInformation.desiredDeliveryTimeZone"
          >
            <el-select
              v-model="form.shippingInformation.desiredDeliveryTimeZone"
              :placeholder="$t('common.pleaseSelect')"
              :disabled="!isAvailableDeliveryTime"
            >
              <el-option
                v-if="!isAvailableDeliveryTime"
                :label="$t('common.placeholder.unavailableTimeZone')"
                :value="undefined"
              ></el-option>
              <el-option
                v-else
                v-for="item in desiredDeliveryTimeZones"
                :key="item.value"
                :value="item.value"
                :label="item.label"
              ></el-option>
            </el-select>
          </el-form-item>
          <el-form-item
            v-if="!isMailDeliveryService && !form.memberInformation.businessAccountFlag"
            :label="$t('common.label.specifyDeliveryNote')"
          >
            <el-select class="mb-1" v-model="form.shippingInformation.delivery_note_id" clearable>
              <el-option v-for="(item, index) in deliveryNotes" :key="index" :label="item.label" :value="item.value">{{
                item.label
              }}</el-option>
            </el-select>
            <span class="whitespace-pre-line text-xs leading-normal block">{{ $t('common.shippingHint') }}</span>
          </el-form-item>
          <el-form-item v-if="visible_bundled_flag" :label="$t('common.label.bundledFlag')">
            <div class="flex flex-col">
              <el-radio-group
                v-model="form.shippingInformation.bundled_flag"
                :disabled="isBundledFlagDisabled"
                class="mb-1"
              >
                <el-radio class="radio-dot" :label="Constants.BUNDLED_FLAG.CANBE">
                  {{ $t('common.action.canBe') }}
                </el-radio>
                <el-radio class="radio-dot" :label="Constants.BUNDLED_FLAG.NONE">
                  {{ $t('common.action.none') }}
                </el-radio>
              </el-radio-group>
              <span class="text-xs leading-normal">{{ $t('common.bundledHint') }}</span>
              <span v-if="isNotBundledRemoteIsland" class="text-xs leading-normal mt-1 text-red">
                {{ $t('common.isNotBundledRemoteIsland') }}
              </span>
              <span v-if="hasSpecificProduct" class="text-xs leading-normal mt-1 text-red">
                {{ $t('common.isNotBundledNomuSilicaLight') }}
              </span>
            </div>
          </el-form-item>
        </template>
      </the-section>

      <!-- Subscription -->
      <the-section v-if="isPeriodic" ref="subscription" :title="$t('ec-line029.content.subscription')">
        <template slot="sub">
          <p class="lg:mx-6 mt-2">{{ $t('ec-line029.content.subscriptionDescription') }}</p>
        </template>

        <el-form-item :label="$t('ec-line029.content.deliveryPattern')" prop="subscription.deliveryPattern">
          <el-select v-model="form.subscription.deliveryPattern" disabled>
            <el-option
              v-for="item in deliveryPatterns"
              :key="item.value"
              :value="item.value"
              :label="item.label"
            ></el-option>
          </el-select>
        </el-form-item>

        <el-form-item :label="$t('ec-line029.content.deliveryInterval')" prop="subscription.deliveryInterval">
          <el-row :gutter="16" type="flex">
            <el-col :span="11">
              <el-select
                v-model="form.subscription.deliveryInterval"
                :placeholder="$t('common.pleaseSelect')"
                :disabled="hasLumpSumPayment"
              >
                <el-option
                  v-for="item in monthly"
                  :key="item.value"
                  :value="item.value"
                  :label="item.label"
                ></el-option>
              </el-select>
            </el-col>
            <el-col :span="13">
              <span class="text-gray-950">{{
                $t('ec-line029.content.deliveryIntervalNote', {
                  date: $date.getDate(firstDeliveryDate),
                })
              }}</span>
            </el-col>
          </el-row>
          <p>{{ $t('ec-line029.content.deliveryIntervalTip') }}</p>
        </el-form-item>

        <el-form-item :label="$t('ec-line029.content.firstDeliveryDate')" prop="subscription.firstDeliveryDate">
          <el-input type="text" :value="Utils.date.formatDateJP(firstDeliveryDate)" disabled></el-input>
          <p>{{ $t('ec-line029.content.firstDeliveryDateTip') }}</p>
        </el-form-item>

        <el-form-item :label="$t('ec-line029.content.secondDeliveryDate')" prop="subscription.secondDeliveryDate">
          <el-input
            type="text"
            :value="Utils.date.formatDateJP(form.subscription.secondDeliveryDate)"
            disabled
          ></el-input>
        </el-form-item>
      </the-section>

      <!-- Payment Information -->
      <the-section
        ref="paymentInformation"
        :title="$t('ec-line029.content.paymentInformation')"
        :sub-title="$t('common.requiredItem')"
      >
        <template #subTitle>
          <el-button v-show="isLoginAmazon" id="changeCustomerPayment" class="section__button">{{
            $t('common.action.editAmazon')
          }}</el-button>
          <div v-show="!isLoginAmazon">
            <span v-if="!isViewMode || isEdit(section.paymentInfo) || !form.paymentInformation.cardSeq">{{
              $t('common.requiredItem')
            }}</span>
            <div v-else>
              <el-button
                class="section__button"
                v-if="
                  getServiceIdByPaymentId(form.paymentInformation.methodOfPayment) ===
                  Constants.PAYMENT_METHOD.CREDIT_CARD
                "
                @click="cardDialog = true"
                >{{ $t('common.action.edit') }}</el-button
              >
            </div>
          </div>
        </template>

        <el-form-item v-if="isLoginAmazon">
          <amapay-brand :amazon-card="form.paymentInformation.amazonCard"></amapay-brand>
          <amazon-logout-button
            class="amazon-logout-button2 mt-5 lg:mt-6"
            @click="
              onClickAmazonLogout(
                amazonProductType,
                ['#AmazonPayButton', '#AmazonPayButton2'],
                Constants.AMAZON.ACCESS_TOKEN_029
              )
            "
          />
        </el-form-item>

        <fragment v-else-if="!isLoginAmazon">
          <el-form-item
            :label="$t('ec-line029.content.methodOfPayment')"
            prop="paymentInformation.methodOfPayment"
            required
          >
            <el-select v-model="form.paymentInformation.methodOfPayment" :placeholder="$t('common.pleaseSelect')">
              <el-option
                v-for="item in paymentMethods"
                :key="item.value"
                :value="item.value"
                :label="item.label"
                :disabled="checkDisablePaymentMethod(item)"
              >
                <div class="flex flex-col lg:items-center lg:flex-row lg:justify-between">
                  <p class="lg:w-1/2">{{ item.label }}</p>
                  <p v-if="checkDisablePaymentMethod(item)" class="text-red-150 text-xs">
                    {{
                      $t('common.paymentMethod.noteRuleMax', {
                        total: $n(item.rule_max / Constants.MAN_TO_YEN_CURRENCY, 'numeral', 'ja-JP'),
                      })
                    }}
                  </p>
                </div>
              </el-option>
            </el-select>
          </el-form-item>

          <div
            v-show="
              getServiceIdByPaymentId(form.paymentInformation.methodOfPayment) === Constants.PAYMENT_METHOD.AMAZON
            "
            class="section-amazon2"
          >
            <div v-if="!isLoginAmazon" class="section-amazon2__button">
              <div
                id="AmazonPayButton2"
                class="section-amazon2__button-login"
                @click="(event) => handleAmazonButton(event, Constants.AMAZON.BACKUP_AMAPAY_029)"
                :class="{ 'pointer-events-none': disableAmazon }"
              ></div>
              <div v-if="user" class="section-amazon2__button-hint">{{ $t('common.amazonHint') }}</div>
              <div v-else class="section-amazon2__button-hint">{{ $t('ec-line029.content.amazonHint2') }}</div>
            </div>
          </div>
          <el-form-item
            v-if="
              form.paymentInformation.cardSeq !== null &&
              isViewMode &&
              !isEdit(section.paymentInfo) &&
              getServiceIdByPaymentId(form.paymentInformation.methodOfPayment) === Constants.PAYMENT_METHOD.CREDIT_CARD
            "
          >
            <payment-info-item
              :card-number="form.paymentInformation.cardNumber"
              :month="form.paymentInformation.dateOfExpiredMonth"
              :year="form.paymentInformation.dateOfExpiredYear"
            />
          </el-form-item>

          <fragment v-if="!form.paymentInformation.cardSeq || !isViewMode || isEdit(section.paymentInfo)">
            <div
              v-if="
                getServiceIdByPaymentId(form.paymentInformation.methodOfPayment) ===
                Constants.PAYMENT_METHOD.CREDIT_CARD
              "
              class="form-item-card"
            >
              <el-form-item :label="$t('ec-line029.content.cardNumber')" prop="paymentInformation.cardNumber">
                <el-input
                  type="text"
                  inputmode="numeric"
                  v-model="form.paymentInformation.cardNumber"
                  :placeholder="$t('common.informationPlaceholder.cardNumberPlaceholder')"
                  :maxlength="19"
                ></el-input>
              </el-form-item>

              <el-form-item :label="$t('ec-line029.content.dateOfExpired')" required>
                <el-row :gutter="6" type="flex">
                  <el-col class="flex-1" :span="11">
                    <el-form-item prop="paymentInformation.dateOfExpiredMonth">
                      <el-input
                        inputmode="numeric"
                        type="text"
                        v-model="form.paymentInformation.dateOfExpiredMonth"
                        :placeholder="$t('common.informationPlaceholder.monthPlaceholder')"
                        :maxlength="2"
                      ></el-input>
                    </el-form-item>
                  </el-col>
                  <el-col :span="1">
                    <div class="text-center">{{ $t('common.dateSeparator') }}</div>
                  </el-col>
                  <el-col class="flex-1" :span="11">
                    <el-form-item prop="paymentInformation.dateOfExpiredYear">
                      <el-input
                        inputmode="numeric"
                        type="text"
                        v-model="form.paymentInformation.dateOfExpiredYear"
                        :placeholder="$t('common.informationPlaceholder.yearPlaceholder')"
                        :maxlength="2"
                      ></el-input>
                    </el-form-item>
                  </el-col>
                </el-row>
              </el-form-item>

              <el-form-item :label="$t('ec-line029.content.cardHolder')" prop="paymentInformation.cardHolder">
                <el-input
                  type="text"
                  v-model="form.paymentInformation.cardHolder"
                  :placeholder="$t('common.informationPlaceholder.cardHolderPlaceholder')"
                  :maxlength="50"
                  autocomplete="new-password"
                ></el-input>
              </el-form-item>

              <el-form-item :label="$t('ec-line029.content.securityCode')" prop="paymentInformation.securityCode">
                <el-input
                  type="text"
                  inputmode="numeric"
                  v-model="form.paymentInformation.securityCode"
                  :placeholder="$t('common.informationPlaceholder.securityCodePlaceholder')"
                  :maxlength="4"
                ></el-input>
              </el-form-item>
              <el-form-item>
                <img :src="Constants.SHOP_CONFIG.BANK_CARD_URL" width="182px" height="114px" />
              </el-form-item>

              <el-form-item :label="$t('ec-line029.content.numberOfPayment')" prop="paymentInformation.numberOfPayment">
                <el-select v-model="form.paymentInformation.numberOfPayment" :placeholder="$t('common.pleaseSelect')">
                  <el-option
                    v-for="item in numberOfPayments"
                    :key="item.value"
                    :value="item.value"
                    :label="item.label"
                  ></el-option>
                </el-select>
              </el-form-item>
            </div>
          </fragment>

          <fragment
            v-if="
              getServiceIdByPaymentId(form.paymentInformation.methodOfPayment) ===
              Constants.PAYMENT_METHOD.DEFERRED_PAYMENT
            "
          >
            <div class="deferred_payment">
              <p>{{ $t('ec-line029.content.atodene_title') }}</p>
              <div class="img_banner">
                <el-link href="https://c.atodene.jp/d-rule/" target="_blank" class="over">
                  <img :src="Constants.SHOP_CONFIG.ATODENE_URL" />
                </el-link>
                <div v-html="$t('ec-line029.content.atodene_adv').join('')"></div>
              </div>
            </div>
          </fragment>
          <fragment
            v-if="getServiceIdByPaymentId(form.paymentInformation.methodOfPayment) === Constants.PAYMENT_METHOD.NP"
          >
            <div class="deferred_payment">
              <div class="img_banner">
                <el-link href="https://np-atobarai.jp/about/" target="_blank" class="over">
                  <img :src="Constants.SHOP_CONFIG.NP_URL" />
                </el-link>
                <div class="bg-gray-100 p-4 mt-2" v-html="getNpAdvText"></div>
              </div>
            </div>
          </fragment>
        </fragment>
      </the-section>

      <the-section :title="$t('ec-line029.content.others')" class="mb-1 lg:mb-4">
        <el-form-item :label="$t('ec-line029.content.placeFindThisProduct')" prop="placeFindThisProduct">
          <el-select v-model="form.placeFindThisProduct" :placeholder="$t('common.pleaseSelect')">
            <el-option
              v-for="item in placeFindProducts"
              :key="item.value"
              :value="item.value"
              :label="item.label"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('ec-line029.content.remarks')">
          <el-input
            type="text"
            v-model="form.advertisement_remarks"
            :placeholder="$t('common.pleaseEnter')"
            :maxlength="255"
          ></el-input>
        </el-form-item>
      </the-section>

      <div v-if="isConfirmSkip" class="confirm-section ec-line004">
        <div class="confirm-section__header">
          <the-heading class="lg:mb-8 mb-5">
            {{ isCartPeriodic ? $t('ec-line003.tab.subscription') : $t('ec-line003.tab.normal') }}
            <br />
            {{ $t('ec-line004.heading') }}
          </the-heading>
          <div>{{ $t('ec-line029.confirmSkipNav') }}</div>
          <div>{{ $t('ec-line029.confirmSkipChangeOrder') }}</div>
          <div class="header_img"><img :src="Constants.SHOP_CONFIG.NAVIGATION_ICON" /></div>
        </div>

        <div class="purchase-detail mt-5 lg:mt-10">
          <the-section :hasSlotTitle="true">
            <template #slotTitle>
              <div class="flex justify-between items-center">
                <h2 class="flex-1">{{ $t('ec-line005.purchaseDetail.title') }}</h2>
              </div>
            </template>

            <div>
              <el-table
                :data="purchase.items"
                class="hidden lg:block table-product"
                :empty-text="$t('common.placeholder.noDataText')"
              >
                <el-table-column :label="$t('ec-line029.productName')" width="450">
                  <template slot-scope="scope">
                    <div class="flex items-center justify-start">
                      <div class="text-base font-bold text-black">{{ scope.row.name }}</div>
                    </div>
                  </template>
                </el-table-column>
                <!-- 個数 -->
                <el-table-column :label="$t('ec-line003.itemTable.quantity')" align="center" width="100">
                  <template slot-scope="scope">
                    <div class="items-center justify-start">
                      <div class="text-base text-black">{{ scope.row.quantity }}</div>
                    </div>
                  </template>
                </el-table-column>
              </el-table>
            </div>

            <div v-for="item in purchase.items" :key="item.id" class="block lg:hidden">
              <div class="cart-item border-b border-solid border-gray-550">
                <div class="flex flex-row space-x-0 lg:space-x-8 gap-2">
                  <div class="flex-1 w-auto flex flex-col">
                    <div class="cart-item__description">{{ item.name }}</div>
                    <div class="cart-item__total">
                      <span>{{ $t('cartList.item.quantity') }}</span>
                      <span>{{ $t('cartList.item.separatorColon') }}</span>
                      <span>{{ item.quantity }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="bg-white pt-0 mb-5">
              <div
                v-if="summary"
                class="
                  flex
                  w-full
                  purchase-detail__total
                  mt-2
                  lg:mt-6
                  border-t border-gray-950 border-opacity-70 border-solid
                "
              >
                <div class="ml-auto text-black mt-4 pl-6 pr-2 py-4 rounded-lg bg-gray">
                  <!-- 送料 -->
                  <div class="flex justify-between items-center text-base lg:text-lg mb-3">
                    <div class="flex items-center text-base lg:text-lg">
                      <span>{{ $t('common.label.deliveryFee') }}</span>
                      <span class="font-normal text-xs">{{ $t('cartList.item.withTax') }}</span>
                    </div>
                    <span>{{ $n(summary.delivery_fee, 'currency', 'ja-JP') }}</span>
                  </div>
                  <!-- 商品合計 -->
                  <div class="payment-total flex font-bold justify-between items-center text-base lg:text-lg">
                    <div class="flex items-center text-base lg:text-lg">
                      <span>{{ $t('common.label.total') }}</span>
                      <span class="font-normal text-xs">{{ $t('cartList.item.withTax') }}</span>
                    </div>
                    <div>
                      <span class="text-2xl">{{ $n(summary.payment_total, 'currency', 'ja-JP') }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="isCartPeriodic">
              <!-- 次回以降の定期購入内容 -->
              <span class="ml-2 mt-5 text-sm lg:text-base font-normal">
                {{ $t('ec-line004.purchaseDetail.subsequentAmount') }}
              </span>

              <div class="bg-white pt-0 mb-5">
                <div
                  v-if="summary"
                  class="
                    flex
                    w-full
                    purchase-detail__total
                    lg:mt-2
                    border-t border-gray-950 border-opacity-70 border-solid
                  "
                >
                  <div class="ml-auto text-black mt-4 pl-6 pr-2 py-4 rounded-lg bg-gray">
                    <!-- 次回配送料金 -->
                    <div class="flex justify-between items-center text-base lg:text-lg mb-3">
                      <div class="flex items-center text-base lg:text-lg">
                        <span>
                          {{ $t('common.label.deliveryFee') }}
                        </span>
                        <span class="font-normal text-xs">
                          {{ $t('cartList.item.withTax') }}
                        </span>
                      </div>
                      <span>
                        {{ $n(summary.next_delivery_fee, 'currency', 'ja-JP') }}
                      </span>
                    </div>
                    <!-- 次回商品合計 -->
                    <div class="payment-total flex font-bold justify-between items-center text-base lg:text-lg">
                      <div class="flex items-center text-base lg:text-lg">
                        <span>
                          {{ $t('common.label.total') }}
                        </span>
                        <span class="font-normal text-xs">
                          {{ $t('cartList.item.withTax') }}
                        </span>
                      </div>
                      <div>
                        <span class="text-2xl">
                          {{ $n(summary.next_payment_total, 'currency', 'ja-JP') }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- 6ヶ月継続での定期購入内容 -->
              <span v-if="!hasLumpSumPayment" class="ml-2 mt-5 text-sm lg:text-base font-normal">
                {{ $t('ec-line004.purchaseDetail.sixMonthSubsequentAmount') }}
              </span>

              <div v-if="!hasLumpSumPayment" class="bg-white pt-0 mb-5">
                <div
                  v-if="summary"
                  class="
                    flex
                    w-full
                    purchase-detail__total
                    lg:mt-2
                    border-t border-gray-950 border-opacity-70 border-solid
                  "
                >
                  <div class="ml-auto text-black mt-4 pl-6 pr-2 py-4 rounded-lg bg-gray">
                    <!-- 6ヶ月配送料金 -->
                    <div class="flex justify-between items-center text-base lg:text-lg mb-3">
                      <div class="flex items-center text-base lg:text-lg">
                        <span>
                          {{ $t('common.label.deliveryFee') }}
                        </span>
                        <span class="font-normal text-xs">
                          {{ $t('cartList.item.withTax') }}
                        </span>
                      </div>
                      <span>
                        {{ $n(summary.six_month_delivery_fee, 'currency', 'ja-JP') }}
                      </span>
                    </div>
                    <!-- 6ヶ月商品合計 -->
                    <div class="payment-total flex font-bold justify-between items-center text-base lg:text-lg">
                      <div class="flex items-center text-base lg:text-lg">
                        <span>
                          {{ $t('common.label.total') }}
                        </span>
                        <span class="font-normal text-xs">
                          {{ $t('cartList.item.withTax') }}
                        </span>
                      </div>
                      <div>
                        <span class="text-2xl">
                          {{ $n(summary.six_month_payment_total, 'currency', 'ja-JP') }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </the-section>
        </div>

        <div class="w-full mt-5">
          <the-section class="text-gray-950 text-base" :title="$t('ec-line029.content.registrationMember')">
            <el-form-item
              v-if="corporateFlag || !form.memberInformation.businessAccountFlag"
              :label="$t('common.memberInformation.fullName')"
            >
              <div class="break-all font-light">
                {{ form.memberInformation.lastName }}{{ ' ' }}{{ form.memberInformation.firstName }}
              </div>
            </el-form-item>

            <el-form-item v-else :label="$t('common.memberInformation.fullName')">
              <div class="break-all font-light">
                {{ form.memberInformation.companyName }}
              </div>
            </el-form-item>

            <el-form-item :label="$t('common.memberInformation.address')">
              <div class="text-address">
                <p>{{ viewPostalCode }}</p>
                <p>{{ buidingAddress }}</p>
                <p>{{ form.memberInformation.buildingApartmentName }}</p>
              </div>
            </el-form-item>
          </the-section>
        </div>

        <div class="w-full mt-5">
          <the-section class="text-gray-950 text-base" :title="$t('ec-line029.content.shippingInformation')">
            <el-form-item :label="$t('common.label.deliveryDate')">
              <div class="break-all font-light">
                {{ Utils.date.formatDateJP(firstDeliveryDate) }}
              </div>
            </el-form-item>
          </the-section>
        </div>

        <div class="w-full mt-5">
          <the-section class="text-gray-950 text-base" :title="$t('ec-line029.content.subscription')">
            <el-form-item :label="$t('common.label.deliveryInterval')">
              <span v-if="!corporateFlag" class="font-light">
                {{ this.form.subscription.deliveryInterval
                }}{{ $t('ec-line029.content.deliveryIntervalNote', { date: $date.getDate(firstDeliveryDate) }) }}
              </span>
            </el-form-item>
          </the-section>
        </div>

        <div class="w-full mt-5">
          <the-section class="text-gray-950 text-base" :title="$t('ec-line029.content.paymentInformation')">
            <el-form-item :label="$t('common.label.paymentMethod')">
              <span class="font-light">
                {{ currentPayment }}
              </span>
            </el-form-item>
            <el-form-item
              v-if="
                getServiceIdByPaymentId(form.paymentInformation.methodOfPayment) ===
                Constants.PAYMENT_METHOD.CREDIT_CARD
              "
              :label="$t('ec-line029.creditCardInformationTitle')"
            >
              <payment-info-item
                :card-number="maskCcNo"
                :month="form.paymentInformation.dateOfExpiredMonth"
                :year="form.paymentInformation.dateOfExpiredYear"
              />
            </el-form-item>
          </the-section>
        </div>

        <payment-information-card :data="form.paymentInformation"></payment-information-card>

        <div class="mt-6 c-form mx-auto px-4 lg:px-0" v-html="commercialLaw"></div>
      </div>

      <iframe :src="Constants.SHOP_CONFIG.TERM_OF_USE_CONTENT" class="mt-3 w-full h-80 bg-gray"></iframe>
      <iframe :src="Constants.SHOP_CONFIG.PRIVACY_CONTENT" class="mt-3 w-full h-80 bg-gray"></iframe>

      <div v-if="!isConfirmSkip" class="w-full mt-6 hidden-backface justify-center flex">
        <el-button class="submit-btn submit-btn--purchase-form submit-btn-skip" @click="onSubmit">
          <span class="text-xs block lg:text-sm">{{ $t('ec-line029.confirmSkipButtonMessage') }}</span>
          <span class="text-base lg:text-lg">{{ $t('ec-line029.content.btnConfirmBottom') }}</span>
        </el-button>
      </div>

      <div v-else class="w-full mt-6 hidden-backface justify-center flex">
        <el-button class="submit-btn submit-btn--purchase-form submit-btn-skip" @click="onSubmit">
          <span class="text-xs block lg:text-sm">{{ $t('ec-line029.confirmSkipButtonMessage') }}</span>
          <span class="text-base lg:text-lg">{{ $t('ec-line004.purchaseConfirmedBtn') }}</span>
        </el-button>
      </div>
    </el-form>
    <address-dialog
      :visible.sync="addressDialog"
      :data="addresses"
      v-model="form.shippingInformation.addressId"
      @updated-address="onUpdatedAddress"
      @update-customer="updateCustomerInfo"
    />
    <card-dialog
      :visible.sync="cardDialog"
      :data="cards"
      v-model="form.paymentInformation.cardSeq"
      @reload-list="getCardList(true)"
    />
    <confirm-dialog
      :visible.sync="visibleToBeFriend"
      :title="$t('ec-line003.message.headingMessage')"
      :content="$t('ec-line003.message.contentMessage')"
      @on-accept="acceptPopup"
    />
  </div>
</template>

<script>
import { cloneDeep, isEqual } from 'lodash'
import mixinAmazon from '@/mixins/amazonV2'
import deliveryPatternMixin from '@/mixins/deliveryPattern'
import deliveryNoteMixin from '@/mixins/deliveryNote'
import TheSection from '@/components/my-page-partials/TheSection.vue'
import TheHeading from '@/components/my-page-partials/TheHeading.vue'
import mixinRegisterCustomer from '@/mixins/register-customer'
import mixinGMO from '@/mixins/gmo'
import mixinLine from '@/mixins/ec-line029/line'
import mixinForm from '@/mixins/ec-line029/form'
import AmazonLogoutButton from '@/components/AmazonLogoutButton.vue'
import AddressInfoItem from '@/components/AddressInfoItem.vue'
import PaymentInfoItem from '@/components/PaymentInfoItem.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import LoginForm from '@/components/ec-line029/LoginForm.vue'
import AddressDialog from '@/components/AddressDialog.vue'
import CardDialog from '@/components/CardDialog.vue'
import {
  PostCodeJpService,
  MyPageService,
  PaymentService,
  AddressService,
  AuthService,
  ProductService,
  DeliveryService,
  CartService,
} from '@/services'
import liff from '@line/liff'
import { changeHalfNumber, syncCart, toHalfWidth, removeEmptyQueryString } from '@/utils'
import {
  COOKIE_ACCESS_TOKEN,
  MAIL_DELIVERY_SERVICE,
  PROVIDER,
  CARD_DEFAULT_FLAG,
  SPECIAL_PRODUCT_NAMES,
} from '@/constants'
import { AMAZON } from '@/constants/amazon'
import { ENV_KEY } from '@/constants/env'
import LineButton from '@/components/LineButton.vue'
import { isEmpty } from 'lodash'
import AmapayBrand from '../AmapayBrand.vue'
import { HtmlService } from '@/services'

export default {
  components: {
    TheHeading,
    TheSection,
    AmazonLogoutButton,
    AddressInfoItem,
    PaymentInfoItem,
    AddressDialog,
    CardDialog,
    LoginForm,
    ConfirmDialog,
    LineButton,
    AmapayBrand,
  },
  props: {
    isPeriodic: Boolean,
    isBack004: Boolean,
  },
  mixins: [mixinAmazon, mixinGMO, mixinForm, mixinRegisterCustomer, mixinLine, deliveryPatternMixin, deliveryNoteMixin],
  data() {
    const dateValidate = (rule, value, callback) => {
      if (
        !this.isLoginAmazon &&
        !(
          this.form.memberInformation.birthdayYear &&
          this.form.memberInformation.birthdayMonth &&
          this.form.memberInformation.birthdayDay
        )
      ) {
        callback(new Error(this.$t('common.message.dateOfBirth')))
      }
      if (
        (this.form.memberInformation.birthdayYear &&
          this.form.memberInformation.birthdayMonth &&
          this.form.memberInformation.birthdayDay) ||
        (this.form.memberInformation.birthdayYear === null &&
          this.form.memberInformation.birthdayMonth === null &&
          this.form.memberInformation.birthdayDay === null)
      ) {
        callback()
      } else {
        callback(new Error(this.$t('common.message.dateOfBirth')))
      }

      callback(new Error(this.$t('common.message.dateOfBirth')))
    }

    const companyNameRules = (rule, value, callback) => {
      if (this.form.memberInformation.businessAccountFlag && !value) {
        callback(new Error(this.$t('common.validate.cmn-err-001', [this.$t('common.validate.field.companyName')])))
      }

      callback()
    }
    return {
      edits: [],
      dateValidate,
      confirmOverrideShippingVisible: false,
      visibleToBeFriend: false,
      purchaseContent1: '',
      purchaseContent2: '',
      isValidatePhoneNumberFlag: false,
      companyNameRules: [{ validator: companyNameRules, trigger: ['blur', 'change'] }],
      commercialLaw: '',
      purchase: {
        items: [],
      },
      summary: null,
      customerEmailFocusOutFlag: false,
      customerConfirmEmailFocusOutFlag: false,
      customerPhoneNumberFocusOutFlag: false,
    }
  },

  computed: {
    isViewMode() {
      return !!this.user
    },
    isHokkaido() {
      return this.form.shippingInformation.prefecture === this.Constants.HOKKAIDO_ID
    },
    desiredDeliveryDays() {
      return this.$store.getters['master/desiredDeliveryDays'](this.deliveryMinDate)
    },
    firstDeliveryDate() {
      return this.calcFirstDeliveryDate(this.form.shippingInformation.desiredDeliveryDay, this.isHokkaido)
    },
    isAddressShippingDiffInfo() {
      return this.form.shippingInformation.deliveryDestination === this.Constants.DELIVERY_DESTINATION.DIFFERENCE_MEMBER
    },
    saleType() {
      return this.isPeriodic ? this.Constants.CART_TYPES.SUBSCRIPTION : this.Constants.CART_TYPES.NORMAL
    },
    isLoggedIn() {
      return this.liffIdExists && liff.isLoggedIn()
    },
    lineChannelRequire() {
      return this.$store.getters['auth/shop'].line_channel_flag
    },
    ruleMaxAmazon() {
      return this.$store.state.register.paymentMethods?.find(
        (i) => i.payment_service_id === this.Constants.PAYMENT_METHOD.AMAZON
      )?.rule_max
    },
    hintAmazon() {
      return !this.totalAmount
        ? this.$t('common.amazon.zeroYenHint')
        : this.$t('common.amazon.hint', {
            total: this.$n(this.ruleMaxAmazon / this.Constants.MAN_TO_YEN_CURRENCY, 'numeral', 'ja-JP'),
          })
    },
    disableAmazon() {
      return (this.ruleMaxAmazon !== null && this.totalAmount > this.ruleMaxAmazon) || !this.totalAmount
    },
    normalCarts() {
      return this.$store.getters['cart/normalCarts']
    },
    subscriptionCarts() {
      return this.$store.getters['cart/subscriptionCarts']
    },
    totalAmount() {
      return this.isPeriodic
        ? Number(this.subscriptionCartsSummary.payment_total)
        : Number(this.normalCartsSummary.payment_total)
    },
    isMailDeliveryService() {
      return (
        this.shippingMethods.find((item) => item.value === this.form.shippingInformation.shippingMethod)?.label ===
        MAIL_DELIVERY_SERVICE
      )
    },
    normalCartsSummary: {
      get() {
        return this.$store.getters['cart/normalCartsSummary']
      },
      set(val) {
        this.$store.commit('cart/SET_NOMAL_CARTS_SUMMARY', val)
      },
    },
    subscriptionCartsSummary: {
      get() {
        return this.$store.getters['cart/subscriptionCartsSummary']
      },
      set(val) {
        this.$store.commit('cart/SET_SUBSCRIPTION_CARTS_SUMMARY', val)
      },
    },
    amountChangeFlag: {
      get() {
        return this.$store.getters['cart/amountChangeFlag']
      },
      set(val) {
        this.$store.commit('cart/SET_AMOUNT_CHANGE_FLAG', val)
      },
    },
    isNotBundledRemoteIsland: {
      get() {
        return this.$store.getters['cart/isNotBundledRemoteIsland']
      },
      set(val) {
        this.$store.commit('cart/SET_IS_NOT_BUNDLED_REMOTE_ISLAND', val)
      },
    },
    hasLumpSumPayment() {
      return this.isPeriodic && this.subscriptionCarts.some((item) => item.lump_sum_payment_flag)
    },
    corporateFlag() {
      return this.$store.getters['auth/shop']?.corporate_flag
    },
    businessAccountUseFlag() {
      return this.$store.getters['auth/shop']?.business_account_use_flag
    },
    fullNameAddress() {
      const fullNameAddress =
        this.form.shippingInformation.storeName +
        this.Utils.helper.fullName(this.form.shippingInformation.lastName, this.form.shippingInformation.firstName)
      return fullNameAddress
    },
    isConfirmSkip() {
      return this.$store.getters['auth/shop']?.confirm_skip_ec_site_flag
    },
    isCartPeriodic() {
      return this.$route.path.includes(this.Constants.CART_TYPE.PERIODIC)
    },
    defrayalECLine004() {
      return `${this.$store.getters['env/GET_KEY'](ENV_KEY.BASE_URL)}${this.Constants.SHOP_CONFIG.DEFRAYAL_EC_LINE004}`
    },
    currentPayment() {
      if (this.isLoginAmazon) {
        return this.paymentMethods.find((item) => item.id === this.Constants.PAYMENT_METHOD.AMAZON)?.label
      } else {
        return this.paymentMethods.find((item) => item.value === this.form.paymentInformation.methodOfPayment)?.label
      }
    },
    maskCcNo() {
      return this.getMaskCcNo()
    },
    viewPostalCode() {
      return this.getPostalCode()
    },
    buidingAddress() {
      return this.excludeBuidingAddress()
    },
    getNpAdvText() {
      const shopId = this.$store.getters['env/GET_KEY'](ENV_KEY.SHOP_ID)
      if (shopId === 6) {
        return this.$t('ec-line029.content.np_adv_nectalaska').join('')
      } else {
        return this.$t('ec-line029.content.np_adv').join('')
      }
    },
    isSubscription: {
      get() {
        return this.$store.getters['cart/subscription']
      },
      set(val) {
        this.$store.commit('cart/SET_SUBSCRIPTION', val)
      },
    },
    shopId() {
      return this.$store.getters['env/GET_KEY'](ENV_KEY.SHOP_ID)
    },
    clientId() {
      return this.$store.getters['env/GET_KEY'](ENV_KEY.CLIENT_ID)
    },
    shouldDetectCartAbandonmentForGuest() {
      return (
        this.customerEmailFocusOutFlag || this.customerConfirmEmailFocusOutFlag || this.customerPhoneNumberFocusOutFlag
      )
    },
    cartAbandonmentFlags() {
      return this.$store.getters['cartTracking/cartAbandonmentFlags']
    },
    isCartAbandonment() {
      const flag = this.cartAbandonmentFlags
      const user = this.user

      if (!flag || !flag.ec_flag) return false

      const isCustomerTarget = flag.customer_target_flag && !!user
      const isGuestTarget = flag.guest_target_flag && !user

      return isCustomerTarget || isGuestTarget
    },
    getPayloadCartTrackingData() {
      return this.$store.getters['cartTracking/cartTrackingData'] || {}
    },
    isUrlClicked() {
      return this.getPayloadCartTrackingData.url_clicked
    },
    default_bundled_flag() {
      return this.$store.getters['auth/shop'].default_bundled_flag
    },
    visible_bundled_flag() {
      return this.$store.getters['auth/shop'].visible_bundled_flag
    },
    hasSpecificProduct() {
      const shopId = this.$store.getters['env/GET_KEY']('SHOP_ID')
      if (shopId !== 1) return false

      const targetNames = [SPECIAL_PRODUCT_NAMES.NOMU_SILICA_LIGHT]
      const targetCarts = this.isPeriodic ? this.subscriptionCarts : this.normalCarts

      const hasSpecificProduct_by_product_name =
        targetCarts.some((cart) => targetNames.some((targetName) => cart.product_name?.includes(targetName))) || false

      return hasSpecificProduct_by_product_name
    },
    isBundledFlagDisabled() {
      return this.isNotBundledRemoteIsland || this.hasSpecificProduct
    },
  },
  watch: {
    firstDeliveryDate() {
      this.getSecondDeliveryDate()
    },
    'form.shippingInformation.addressId'(val) {
      this.updateAddress(val)
      this.updateDeliveryMethod(this.postalCodeShipping)
      this.updateMinDeliveryDate(this.postalCodeShipping, this.form.shippingInformation.prefecture)
      this.checkNotBundledRemoteIsland(this.postalCodeShipping)
      this.checkSpecificProductBundledFlag()
    },
    'form.paymentInformation.cardSeq'(val) {
      this.updateCard(val)
    },
    async isLoginAmazon(val) {
      if (val) {
        this.form.paymentInformation.methodOfPayment = this.getPaymentIdByServiceId(
          this.Constants.PAYMENT_METHOD.AMAZON
        )
      } else {
        this.form.paymentInformation.methodOfPayment = this.getPaymentIdByServiceId(
          this.Constants.PAYMENT_METHOD.CREDIT_CARD
        )
        this.form.paymentInformation.amazonCard = null

        if (this.user) {
          await this.fetchLoggedIn()
        }
      }
    },
    'form.paymentInformation.methodOfPayment'() {
      if (this.user) return
      this.form.paymentInformation = {
        ...this.form.paymentInformation,
        cardNumber: '',
        dateOfExpiredMonth: '',
        dateOfExpiredYear: '',
        cardHolder: null,
        securityCode: '',
        cardSeq: null,
      }
    },

    'form.subscription.deliveryInterval'() {
      this.getSecondDeliveryDate()
    },
    paymentMethods() {
      this.$nextTick(() => this.handleChangeMethodOfPayment())
    },
    'form.memberInformation.businessAccountFlag'(val) {
      if (!val) {
        this.form.memberInformation.companyName = ''
      }
    },
    shouldDetectCartAbandonmentForGuest(flag) {
      if (flag) {
        this.fetchDetectCartAbandonment()
        this.customerEmailFocusOutFlag = false
        this.customerConfirmEmailFocusOutFlag = false
        this.customerPhoneNumberFocusOutFlag = false
      }
    },
  },
  async created() {
    const loading = this.$loading({
      lock: true,
    })

    this.initAmazonSessionId(AMAZON.ACCESS_TOKEN_029).then(() => {
      this.setDisiredDeliverydays()
      this.initInfo()
    })

    this.getCommercialLaw()
    this.initPurchase()
    this.form.shippingInformation.bundled_flag = this.default_bundled_flag
    try {
      await Promise.allSettled([this.getShippingMethod(), this.getSecondDeliveryDate()])

      if (this.user) {
        await this.fetchLoggedIn()
        const postalCode = await this.getPostalCode()
        this.checkNotBundledRemoteIsland(postalCode)
        this.detectCartAbandonmentWithFlag()
      } else if (this.$cookies.get(AMAZON.CART_LINE029)) {
        this.restoreFromCookies()
      } else {
        this.returnSet()
      }
    } catch (error) {
      this.handleError(error)
    } finally {
      loading.close()
    }
  },
  mounted() {
    const loading = this.$loading({
      lock: true,
    })
    this.getAmazonButton(this.amazonProductType, ['#AmazonPayButton', '#AmazonPayButton2'])
      .then(() => {
        if (this.$session[AMAZON.BACKUP_AMAPAY_029]) {
          const payload = JSON.parse(this.$session[AMAZON.BACKUP_AMAPAY_029])

          this.form = cloneDeep(payload)
        }
        return Promise.resolve()
      })
      .then(() => this.getAmazonShiping(AMAZON.ACCESS_TOKEN_029))
      .then(({ data }) => {
        if (this.isBack004) {
          if (!this.isLoginAmazon) {
            this.form.paymentInformation.methodOfPayment = this.getPaymentIdByServiceId(
              this.Constants.PAYMENT_METHOD.CREDIT_CARD
            )
            this.form.paymentInformation.amazonCard = null
          }
          return
        }

        if (data) {
          if (!this.user) {
            this.fillAmazonCustomerInfo(data, this.form.memberInformation)

            if (this.$session[AMAZON.BACKUP_AMAPAY_029]) {
              this.$confirm(this.$t('common.message.overrideAmazonShipping'), '', {
                confirmButtonText: this.$t('common.label.yes'),
                cancelButtonText: this.$t('common.label.no'),
                showClose: false,
                customClass: 'max-w-xs',
              })
                .then(() => {
                  if (this.isSameBillingAndShipping(data)) {
                    this.form.shippingInformation.deliveryDestination = this.Constants.DELIVERY_DESTINATION.SAME_MEMBER
                  } else {
                    this.form.shippingInformation.deliveryDestination =
                      this.Constants.DELIVERY_DESTINATION.DIFFERENCE_MEMBER
                    this.fillAmazonAddressInfo(data, this.form.shippingInformation)
                  }
                })
                .catch(() => {
                  // Do nothing
                })
                .finally(() => {
                  this.form.paymentInformation.methodOfPayment = this.getPaymentIdByServiceId(
                    this.Constants.PAYMENT_METHOD.AMAZON
                  )
                })
            } else {
              if (this.isSameBillingAndShipping(data)) {
                this.form.shippingInformation.deliveryDestination = this.Constants.DELIVERY_DESTINATION.SAME_MEMBER
              } else {
                this.form.shippingInformation.deliveryDestination =
                  this.Constants.DELIVERY_DESTINATION.DIFFERENCE_MEMBER
                this.fillAmazonAddressInfo(data, this.form.shippingInformation)
              }
            }
          } else if (this.isLoginAmazon) {
            this.form.paymentInformation.methodOfPayment = this.getPaymentIdByServiceId(
              this.Constants.PAYMENT_METHOD.AMAZON
            )
          }

          this.detectCartAbandonmentWithFlag()
          this.$session[AMAZON.BACKUP_AMAPAY_029] = null
          this.fillAmazonPaymentInfo(data, this.form.paymentInformation)
        }
      })
      .then(() => {
        this.$nextTick(() => {
          if (!this.user) {
            this.bindingAction('changeCustomerAddress', this.form, AMAZON.ACCESS_TOKEN_029, AMAZON.BACKUP_AMAPAY_029)
          }
          this.bindingAction('changeCustomerPayment', this.form, AMAZON.ACCESS_TOKEN_029, AMAZON.BACKUP_AMAPAY_029)
        })
      })
      .then(() => this.clearValidate()),
      this.fetchPurchaseContent().finally(() => loading.close())
  },
  methods: {
    clearValidate() {
      this.$refs.form.clearValidate()
    },
    /**
     * Fetch on logged in
     */
    async fetchLoggedIn() {
      await Promise.allSettled([this.getCardList(), this.getListAddress()])
    },

    acceptPopup() {
      if (this.liffIdExists) {
        !this.user && this.setCartToCookies()

        if (this.user && this.user.line_id) {
          window.location.href = `https://line.me/R/ti/p/~${this.$store.getters['env/GET_KEY'](ENV_KEY.LINE_BOT_ID)}`
          return
        }

        liff.login({
          redirectUri: `${this.$store.getters['env/GET_KEY'](ENV_KEY.BASE_URL)}${process.env.VUE_APP_PREFIX}${
            this.$routePage.CALLBACK
          }?redirect_url=${this.$route.path}&user_status=${
            this.user ? this.Constants.USER_STATUS.LOGGED : this.Constants.USER_STATUS.NOT_LOGGED
          }`,
        })
      }
    },

    cloneCustomerInformation() {
      if (this.form.shippingInformation.deliveryDestination === this.Constants.DELIVERY_DESTINATION.SAME_MEMBER) {
        this.form.shippingInformation = {
          ...this.form.shippingInformation,
          lastName: this.form.memberInformation.lastName,
          firstName: this.form.memberInformation.firstName,
          lastNameKana: this.form.memberInformation.lastNameKana,
          firstNameKana: this.form.memberInformation.firstNameKana,
          postalCode1: this.form.memberInformation.postalCode1,
          postalCode2: this.form.memberInformation.postalCode2,
          prefecture: this.form.memberInformation.prefecture,
          municipality: this.form.memberInformation.municipality,
          address: this.form.memberInformation.address,
          buildingApartmentName: this.form.memberInformation.buildingApartmentName,
          phoneNumber: this.form.memberInformation.phoneNumber,
          companyName: this.form.memberInformation.companyName,
          companyNameKana: this.form.memberInformation.companyNameKana,
        }
      }
    },

    /**
     * Enable edit mode
     */
    onEdit(section) {
      this.edits.push(section)
    },

    /**
     * Check form is edit mode
     */
    isEdit(section) {
      return this.edits.includes(section)
    },

    /**
     * Handle find address from postal code
     */
    async getAddress(currentInformation, notUpdateMunicipality = false) {
      const formSection = this.form[currentInformation]

      formSection.postalCode1 = changeHalfNumber(formSection.postalCode1)
      formSection.postalCode2 = changeHalfNumber(formSection.postalCode2)

      if (!formSection.postalCode1?.trim() || !formSection.postalCode2?.trim()) return

      const res = await PostCodeJpService.getAddress({
        postalCode: formSection.postalCode1 + formSection.postalCode2,
      })

      if (!res) {
        formSection.prefecture = null
        return
      }

      formSection.prefecture = this.prefectures.find((item) => item.name === res.prefecture)?.id

      if (!notUpdateMunicipality) {
        formSection.municipality = toHalfWidth(res.municipality)
        formSection.address = toHalfWidth(res.addr02)
      }
    },
    onChangePostalCodeForm(currentInformation) {
      const formSection = this.form[currentInformation]

      formSection.postalCode1 = changeHalfNumber(formSection.postalCode1)
      formSection.postalCode2 = changeHalfNumber(formSection.postalCode2)
    },

    /**
     * Fetch shipping method
     */
    async getShippingMethod() {
      const data = await this.fetchShippingMethod()

      if (data.length && !this.form.shippingInformation.shippingMethod) {
        this.form.shippingInformation.shippingMethod = data[0].id
      }
      this.onUpdateShippingMethod(this.form.shippingInformation.shippingMethod)
    },

    /**
     * Get list card
     */
    async getCardList(isUpdateCard = false) {
      const { data } = await MyPageService.getCardList({ customer_id: this.user.id })
      this.cards = (data || []).slice().reverse()

      if (!this.cards.length) return

      if (this.form.paymentInformation.cardSeq === null) {
        const index = this.cards.findIndex((item) => item.default_flag === CARD_DEFAULT_FLAG.DEFAULT)
        this.updateCard(this.cards[index > -1 ? index : 0].card_seq)
      }

      if (isUpdateCard) {
        this.updateCard(this.form.paymentInformation.cardSeq)
      }
    },

    /**
     * Verify credit card
     */
    async verifyCard() {
      try {
        // Generate token
        const { cardNumber, dateOfExpiredMonth, dateOfExpiredYear, cardHolder, securityCode } =
          this.form.paymentInformation

        const token = await this.getTokenCard({
          holdername: cardHolder,
          cardno: cardNumber,
          expire: `20${dateOfExpiredYear}${dateOfExpiredMonth.padStart(2, '0')}`,
          securitycode: securityCode,
        })

        // Verify card
        if (token?.length) {
          await PaymentService.checkCard({ amount: this.total(this.saleType), token: token[0] })
        }

        return true
      } catch (error) {
        this.handleError(error)

        return false
      }
    },

    /**
     * Get list address of user
     */
    async getListAddress() {
      const { data } = await AddressService.getListAddress()

      this.addresses = data || []

      if (!data.length) return

      if (this.form.shippingInformation.addressId === null) {
        const address = data.find((item) => item.is_default) || data[0]

        this.updateAddress(address.id)
      }
    },

    /**
     * Get label prefecture from label
     */
    getPrefectureFromId(id) {
      return this.prefectures.find((item) => item.id === id)?.name || ''
    },

    /**
     * Get id prefecture from label
     */
    getPrefectureFromLabel(label) {
      return this.prefectures.find((item) => item.label === label)?.value
    },

    /**
     * Update address to form
     */
    updateAddress(id) {
      const shippingInfo = this.addresses?.find((item) => item.id === id)

      if (!shippingInfo) return

      const [postalCode1, postalCode2] = shippingInfo.postal_code?.split(this.$t('common.hyphenSeparator')) || ['', '']

      this.form.shippingInformation = {
        ...this.form.shippingInformation,
        addressId: shippingInfo.id,
        lastName: shippingInfo.name01 || '',
        firstName: shippingInfo.name02 || '',
        lastNameKana: shippingInfo.kana01 || '',
        firstNameKana: shippingInfo.kana02 || '',
        postalCode1: postalCode1 || '',
        postalCode2: postalCode2 || '',
        prefecture: shippingInfo.pref?.id || '',
        municipality: shippingInfo.addr01 || '',
        address: shippingInfo.addr02 || '',
        buildingApartmentName: shippingInfo.addr03 || '',
        phoneNumber: this.Utils.helper.getOnlyNumber(shippingInfo.phone_number),
        storeName: this.corporateFlag && shippingInfo.store_name ? shippingInfo.store_name : '',
        is_update_customer: false,
        is_default: true,
        companyName: this.corporateFlag
          ? shippingInfo.company_name
          : this.user.business_account_flag
          ? shippingInfo.name01 + (shippingInfo.name02 || '')
          : '',
        companyNameKana:
          !this.corporateFlag && this.user.business_account_flag
            ? shippingInfo.kana01 + (shippingInfo.kana02 || '')
            : '',
      }
    },

    /**
     * Update card info to form
     */
    updateCard(cardSeq) {
      const card = this.cards?.find((item) => item.card_seq === cardSeq)

      if (!card) return

      this.form.paymentInformation = {
        ...this.form.paymentInformation,
        dateOfExpiredMonth: card.expire.slice(2),
        dateOfExpiredYear: card.expire.slice(0, 2),
        cardSeq: card.card_seq,
        cardHolder: card.holder_name,
        cardNumber: card.card_no,
      }
    },
    /**
     * Get list payment methods
     */
    async getPaymentMethods() {
      const data = await this.fetchPaymentMethods({ delivery_id: this.form.shippingInformation.shippingMethod })
      // set default payment method to first item
      if (data.length) {
        this.form.paymentInformation.methodOfPayment = data[0]?.id
      } else {
        this.form.paymentInformation.methodOfPayment = undefined
      }
    },

    /**
     * Get second delivery date
     */
    async getSecondDeliveryDate() {
      const res = await this.calcSecondDeliveryDate({
        deliveryInterval: this.form.subscription.deliveryInterval,
        nextDeliveryDate: this.firstDeliveryDate,
        firstDeliveryDate: this.firstDeliveryDate,
      })

      this.form.subscription.secondDeliveryDate = res
    },

    async onUpdateShippingMethod(val) {
      if (!val) {
        this.$store.commit('register/SET_DESIRED_DELIVERY_TIME_ZONE', [])
        return
      }

      await this.getPaymentMethods()

      await this.fetchDeliveryTimezone(val)

      const shippingFee = this.$store.state.register.shippingMethods.find((i) => i.id === val)?.flat_rate_delivery_fee

      if (shippingFee) {
        this.$store.commit('cart/SET_SHIPPING_FEE', shippingFee)
      }

      if (
        !this.desiredDeliveryTimeZones.some((i) => i.value === this.form.shippingInformation.desiredDeliveryTimeZone)
      ) {
        this.form.shippingInformation.desiredDeliveryTimeZone = null
      }
    },
    loginLine() {
      if (this.liffIdExists) {
        !this.user && this.setCartToCookies()

        liff.login({
          redirectUri: `${this.$store.getters['env/GET_KEY'](ENV_KEY.BASE_URL)}${process.env.VUE_APP_PREFIX}${
            this.$routePage.CALLBACK
          }?action=${this.Constants.ACTION_LINE.LOGIN_LINE}&redirect_url=${this.$route.path}`,
        })
      }
    },
    loginByMail(form) {
      const loading = this.$loading({
        lock: true,
      })

      AuthService.loginCustomer({
        login_id: form.email,
        password: form.password,
        client_id: this.$store.getters['env/GET_KEY'](ENV_KEY.CLIENT_ID),
        shop_id: this.$store.getters['env/GET_KEY'](ENV_KEY.SHOP_ID),
      })
        .then(({ data }) => {
          this.$cookies.set(COOKIE_ACCESS_TOKEN, data.access)
        })
        .then(() => this.$store.dispatch('auth/getAuth', { provider: PROVIDER.CUSTOMER }))
        .then(() => this.fetchLoggedIn())
        .then(() => syncCart())
        .then(() => this.getInfoCustomer())
        .then(() => this.detectCartAbandonmentWithFlag())
        .catch((err) => this.handleError(err))
        .finally(() => loading.close())
    },
    checkDisablePaymentMethod(item) {
      return item.rule_max !== null && item.rule_max < this.totalAmount
    },

    handleChangeMethodOfPayment() {
      const paymentMethod = this.paymentMethods.find(
        (item) => item.value === this.form.paymentInformation.methodOfPayment
      )

      if (!isEmpty(paymentMethod) && paymentMethod?.rule_max !== null && paymentMethod?.rule_max < this.totalAmount) {
        paymentMethod.value === this.form.paymentInformation.methodOfPayment &&
          (this.form.paymentInformation.methodOfPayment = '')
        if (this.isLoginAmazon && this.disableAmazon) {
          this.onAmazonLogout(AMAZON.ACCESS_TOKEN_029)
          this.handleError({ message: this.hintAmazon })
        }
      }
    },
    onUpdatedAddress() {
      this.getListAddress()
        .then(() => this.updateAddress(this.form.shippingInformation.addressId))
        .then(() => this.checkNotBundledRemoteIsland(this.postalCodeShipping))
    },
    updateCustomerInfo() {
      const [postalCode1, postalCode2] = this.user.postal_code?.split(this.$t('common.hyphenSeparator')) || ['', '']
      this.form.memberInformation = {
        ...this.form.memberInformation,
        lastName: this.user.name01 || '',
        firstName: this.user.name02 || '',
        lastNameKana: this.user.kana01 || '',
        firstNameKana: this.user.kana02 || '',
        postalCode1: postalCode1 || '',
        postalCode2: postalCode2 || '',
        prefecture: this.user.pref?.id || null,
        municipality: this.user.addr01 || '',
        address: this.user.addr02 || '',
        buildingApartmentName: this.user.addr03 || '',
        phoneNumber: this.Utils.helper.getOnlyNumber(this.user.phone_number),
        companyName: this.corporateFlag
          ? this.user.this.company_name || ''
          : this.user.name01 + (this.user.name02 || ''),
        companyNameKana: this.user.kana01 + (this.user.kana02 || ''),
        businessAccountFlag: this.user.business_account_flag || false,
      }
    },
    async fetchCartItems() {
      const [normalCarts, subscriptionCarts] = await Promise.all([
        this.fetchNormalItems(),
        this.fetchSubscriptionItems(),
      ])

      const carts = [...normalCarts, ...subscriptionCarts]
      this.$store.commit('cart/SET_CART', carts)
    },
    async fetchNormalItems() {
      const product_sku_ids = this.normalCarts.map((i) => i.id + ':' + i.quantity).join()
      const beforeSummary = cloneDeep(this.normalCartsSummary)

      if (product_sku_ids) {
        const { data } = await ProductService.getProductSku({
          is_subscription: 0,
          product_sku_ids,
          customer_id: this.user?.id,
          delivery_method_id: this.form.shippingInformation.shippingMethod,
        })

        const items = this.normalCarts.map((i) => {
          const sku_detail = data.items.find((j) => j.id === i.id)
          return Object.assign({
            ...i,
            ...sku_detail,
            product_image:
              (sku_detail?.product_image &&
                `${this.$store.getters['env/GET_KEY'](ENV_KEY.IMAGE_API)}/${sku_detail.product_image}`) ||
              null,
          })
        })
        this.normalCartsSummary = data.summary
        this.amountChangeFlag = !isEqual(beforeSummary, data.summary)

        return items
      }
      return []
    },
    async fetchSubscriptionItems() {
      const product_sku_ids = this.subscriptionCarts.map((i) => i.id + ':' + i.quantity).join()
      const beforeSummary = cloneDeep(this.subscriptionCartsSummary)

      if (product_sku_ids) {
        const { data } = await ProductService.getProductSku({
          is_subscription: 1,
          product_sku_ids,
          customer_id: this.user?.id,
          delivery_method_id: this.form.shippingInformation.shippingMethod,
        })

        const items = this.subscriptionCarts.map((i) => {
          const sku_detail = data.items.find((j) => j.id === i.id)
          return Object.assign({
            ...i,
            ...sku_detail,
            product_image: `${this.$store.getters['env/GET_KEY'](ENV_KEY.IMAGE_API)}/${sku_detail?.product_image}`,
          })
        })
        this.subscriptionCartsSummary = data.summary
        this.subscriptionCartsSummary.six_month_payment_total = Number(this.subscriptionCartsSummary.payment_total)
        this.subscriptionCartsSummary.six_month_payment_total +=
          Number(this.subscriptionCartsSummary.next_payment_total) * 5
        this.subscriptionCartsSummary.six_month_delivery_fee = Number(this.subscriptionCartsSummary.delivery_fee) * 6
        this.amountChangeFlag = !isEqual(beforeSummary, data.summary)

        return items
      }
      return []
    },
    async fetchPurchaseContent() {
      const res1 = await HtmlService.getHtmlContent(this.Constants.SHOP_CONFIG.PURCHASE_CONTENT1)
      this.purchaseContent1 = res1.data
      const res2 = await HtmlService.getHtmlContent(this.Constants.SHOP_CONFIG.PURCHASE_CONTENT2)
      this.purchaseContent2 = res2.data
    },
    async checkNotBundledRemoteIsland(postalCode) {
      if (!postalCode || postalCode.length !== 8) {
        this.isNotBundledRemoteIsland = false
        return
      }

      const params = {
        delivery_id: this.form.shippingInformation.shippingMethod,
        postal_code: postalCode,
      }

      const { data } = await DeliveryService.checkNotBundledRemoteIsland(params)

      this.isNotBundledRemoteIsland = data.is_remote_island
      if (data.is_remote_island) {
        this.form.shippingInformation.bundled_flag = false
      }
    },
    checkSpecificProductBundledFlag() {
      if (this.hasSpecificProduct) {
        this.form.shippingInformation.bundled_flag = false
      }
    },
    getPostalCode() {
      if (
        !this.user &&
        this.form.shippingInformation.deliveryDestination === this.Constants.DELIVERY_DESTINATION.SAME_MEMBER
      ) {
        return [this.form.memberInformation.postalCode1, this.form.memberInformation.postalCode2].join(
          this.$t('common.hyphenSeparator')
        )
      }

      return [this.form.shippingInformation.postalCode1, this.form.shippingInformation.postalCode2].join(
        this.$t('common.hyphenSeparator')
      )
    },
    excludeBuidingAddress() {
      return `${this.getPrefectureFromId(this.form.memberInformation.prefecture)}
      ${this.form.memberInformation.municipality}${this.form.memberInformation.address}`
    },
    async getCommercialLaw() {
      const { data } = await HtmlService.getHtmlContent(this.defrayalECLine004)

      return (this.commercialLaw = data)
    },
    getMaskCcNo() {
      let maskNo = ''
      const ccNo =
        this.form.paymentInformation.cardNumber === undefined || this.form.paymentInformation.cardNumber === null
          ? ''
          : String(this.form.paymentInformation.cardNumber)
      const ccNoLength = ccNo.length
      if (ccNoLength > 4) {
        let i = ccNoLength - 4
        while (i < ccNoLength) {
          i++
          maskNo += '*'
        }
        maskNo += ccNo.slice(-4)
      } else {
        maskNo = ccNo
      }
      return maskNo
    },
    async initPurchase() {
      const [normalCarts, subscriptionCarts] = await Promise.all([
        this.fetchNormalItems(),
        this.fetchSubscriptionItems(),
      ])

      const purchaseItem = [...normalCarts, ...subscriptionCarts]
      const carts = purchaseItem?.filter(
        (item) =>
          this.saleType === Number(item.subscription) &&
          ![item.is_product_sku_valid, item.visible].includes(false) &&
          item.is_out_stock === false
      )

      if (!carts?.length) return

      const items = carts.map((cart) => ({
        id: cart.id,
        name: cart.product_name,
        img: cart.product_image,
        price: cart.subscription ? cart.price03_after_tax : cart.price02_after_tax,
        subtotal: cart.subscription ? cart.product_subtotal_price03_after_tax : cart.product_subtotal_price02_after_tax,
        quantity: cart.quantity,
        nextPrice: cart.subscription ? cart.next_price03_after_tax : null,
        nextSubtotal: cart.subscription ? cart.next_subtotal_price03_after_tax : null,
        nextDiscountAmount: cart.next_subtotal_discount_amount,
      }))

      this.purchase.items = items

      this.summary = this.isCartPeriodic
        ? this.$store.state.cart.subscriptionCartsSummary
        : this.$store.state.cart.normalCartsSummary

      this.checkSpecificProductBundledFlag()
    },
    async fetchDetectCartAbandonment() {
      Promise.all([this.validatePhoneNumber(), this.validateEmail()]).then(async ([isPhoneValid, isEmailValid]) => {
        if (isPhoneValid || isEmailValid) {
          const toDayDate = new Date().getTime()
          const cartItems = this.isSubscription
            ? this.subscriptionCarts.map((item) => ({
                product_id: item.product_id,
                product_sku_id: item.id,
                quantity: item.quantity,
                price: item.price03_after_tax,
              }))
            : this.normalCarts.map((item) => ({
                product_id: item.product_id,
                product_sku_id: item.id,
                quantity: item.quantity,
                price: item.price02_after_tax,
              }))

          const body = {
            id: localStorage.getItem('tracking_id') ? localStorage.getItem('tracking_id') : null,
            client_id: this.clientId,
            shop_id: this.shopId,
            tracking_start_date: toDayDate,
            customer_id: this.user ? this.user.id : null,
            email: this.form.memberInformation.email && isEmailValid ? this.form.memberInformation.email : null,
            phone_number:
              this.form.memberInformation.phoneNumber && isPhoneValid ? this.form.memberInformation.phoneNumber : null,
            line_id: this.user ? this.user.line_id : null,
            device_type_id: this.Utils.helper.deviceType(this.$store.state.auth.shop),
            payment_total: this.isSubscription
              ? Number(this.subscriptionCartsSummary.payment_total)
              : Number(this.normalCartsSummary.payment_total),
            adjustment_amount: this.isSubscription
              ? Number(this.subscriptionCartsSummary.adjustment_amount)
              : Number(this.normalCartsSummary.adjustment_amount),
            discount_amount: this.isSubscription
              ? Number(this.subscriptionCartsSummary.discount_amount_total)
              : Number(this.normalCartsSummary.discount_amount_total),
            delivery_fee: this.isSubscription
              ? Number(this.subscriptionCartsSummary.delivery_fee)
              : Number(this.normalCartsSummary.delivery_fee),
            subscription_flag: this.isSubscription,
            cart_items: [...cartItems],
            cart_type_id: this.Constants.SYSTEM_TYPE.SHOP_EC,
            url_clicked: this.isUrlClicked,
          }

          const responseUpdateCartAbandonmentSetting = await CartService.updateCartAbandonmentSetting(
            removeEmptyQueryString(body)
          )

          localStorage.setItem('tracking_id', responseUpdateCartAbandonmentSetting.data.id)

          this.$store.dispatch('cartTracking/SET_CART_TRACKING', {
            tracking_id: responseUpdateCartAbandonmentSetting.data.id,
            cart_type_id: this.Constants.SYSTEM_TYPE.SHOP_EC,
            tracking_start_date: responseUpdateCartAbandonmentSetting.data.tracking_start_date,
            customer_id: responseUpdateCartAbandonmentSetting.data.customer_id,
            email: responseUpdateCartAbandonmentSetting.data.email,
            phone_number: responseUpdateCartAbandonmentSetting.data.phone_number,
            line_id: responseUpdateCartAbandonmentSetting.data.line_id,
            url_clicked: this.isUrlClicked,
          })
        }
      })
    },
    detectGuestCartAbandonmentOnBlur(field) {
      if (this.cartAbandonmentFlags.ec_flag && this.cartAbandonmentFlags.guest_target_flag && !this.user) {
        if (field === 'memberInformation.email') {
          this.customerEmailFocusOutFlag = true
        } else if (field === 'memberInformation.emailConfirm') {
          this.customerConfirmEmailFocusOutFlag = true
        } else if (field === 'memberInformation.phoneNumber') {
          this.customerPhoneNumberFocusOutFlag = true
        }
      }
    },
    hasCartItemDifference(newItems, oldItems) {
      if (!Array.isArray(newItems) || !Array.isArray(oldItems)) return true
      if (newItems.length !== oldItems.length) return true
      return newItems.some((newItem) => {
        const matchingOldItem = oldItems.find(
          (oldItem) => oldItem.product_id === newItem.product_id && oldItem.product_sku_id === newItem.product_sku_id
        )
        if (!matchingOldItem) return true
        return Number(newItem.quantity) !== Number(matchingOldItem.quantity)
      })
    },
    detectCartAbandonmentWithFlag() {
      if (this.isCartAbandonment) {
        this.fetchDetectCartAbandonment()
      }
    },
    validatePhoneNumber() {
      return new Promise((resolve) => {
        if (this.user) {
          resolve(true)
        } else {
          this.$refs.form.validateField('memberInformation.phoneNumber', (errorMessage) => {
            resolve(!errorMessage)
          })
        }
      })
    },
    validateEmail() {
      return new Promise((resolve) => {
        if (this.user) {
          resolve(true)
        } else {
          this.$refs.form.validateField('memberInformation.email', (errorMessage) => {
            resolve(!errorMessage)
          })
        }
      })
    },
    returnSet() {
      if (this.form.shippingInformation.deliveryDestination == this.Constants.DELIVERY_DESTINATION.SAME_MEMBER) {
        this.form.shippingInformation.lastName = ''
        this.form.shippingInformation.firstName = ''
        this.form.shippingInformation.lastNameKana = ''
        this.form.shippingInformation.firstNameKana = ''
        this.form.shippingInformation.postalCode1 = ''
        this.form.shippingInformation.postalCode2 = ''
        this.form.shippingInformation.prefecture = null
        this.form.shippingInformation.municipality = ''
        this.form.shippingInformation.address = ''
        this.form.shippingInformation.buildingApartmentName = ''
        this.form.shippingInformation.phoneNumber = ''
        this.form.shippingInformation.companyName = ''
        this.form.shippingInformation.companyNameKana = ''
      }
    },
  },
}
</script>

<style lang="scss">
@import '@/assets/css/components/ec-line029/purchase-form.scss';
</style>
